/* Shared information-section behavior. Delegated handlers survive theme-editor reloads. */
(() => {
  if (window.nomosuInformationEditor) return;
  window.nomosuInformationEditor = true;
  const sliders = new Map();
  const mobile = window.matchMedia('(max-width: 767px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setExpanded(button, expanded) {
    const content = document.getElementById(button.getAttribute('aria-controls'));
    if (!content) return;
    button.setAttribute('aria-expanded', String(expanded));
    if (button.classList.contains('informational-toggle')) {
      button.closest('.informational').classList.toggle('expanded', expanded);
    } else if (button.classList.contains('itf-toggle')) {
      content.classList.toggle('is-collapsed', !expanded);
      button.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
      limitImageText(content);
    } else {
      content.classList.toggle('show', expanded);
      button.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('.informational-toggle, .itf-toggle, .ingredient-detail-section .readmore-btn');
    if (button) setExpanded(button, button.getAttribute('aria-expanded') !== 'true');
  });

  function limitImageText(text) {
    const limit = Math.max(0, Number(text.dataset.limit) || 3);
    text.querySelectorAll('p').forEach((paragraph, index) => {
      paragraph.style.display = text.classList.contains('is-collapsed') && index >= limit ? 'none' : '';
    });
  }

  function destroySlider(root) {
    const state = sliders.get(root);
    if (!state) return;
    clearInterval(state.timer);
    clearTimeout(state.resetTimer);
    state.abort.abort();
    state.track.querySelectorAll('.is-clone').forEach((clone) => clone.remove());
    state.track.style.transform = '';
    state.track.style.transition = '';
    sliders.delete(root);
  }

  function initSlider(root) {
    destroySlider(root);
    if (!mobile.matches) return;
    const track = root.querySelector('.ingredients-track');
    if (!track) return;
    const slides = Array.from(track.querySelectorAll('.ingredient-slide'));
    if (slides.length < 2) return;
    const state = { track, slides, index: 0, abort: new AbortController() };
    sliders.set(root, state);
    const options = { signal: state.abort.signal };
    const pause = () => { clearInterval(state.timer); state.timer = null; };
    const show = (index, animate = true) => {
      state.index = index;
      track.style.transition = animate && !reducedMotion.matches ? 'transform .8s ease' : 'none';
      track.style.transform = `translateX(-${index * slides[0].getBoundingClientRect().width}px)`;
    };
    state.show = show;
    state.pause = pause;
    const clone = slides[0].cloneNode(true);
    clone.classList.add('is-clone');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('data-shopify-editor-block');
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => node.removeAttribute('data-shopify-editor-block'));
    track.appendChild(clone);
    const advance = () => {
      show(state.index + 1);
      if (state.index === slides.length) {
        state.resetTimer = setTimeout(() => show(0, false), reducedMotion.matches ? 0 : 800);
      }
    };
    const resume = () => {
      pause();
      if (root.dataset.autoplay === 'true' && !reducedMotion.matches && !window.Shopify?.designMode) {
        state.timer = setInterval(advance, Math.max(3, Number(root.dataset.interval) || 4) * 1000);
      }
    };
    let touchStart = null;
    track.addEventListener('touchstart', (event) => { touchStart = event.touches[0].clientX; pause(); }, { ...options, passive: true });
    track.addEventListener('touchend', (event) => {
      if (touchStart === null) return;
      const distance = touchStart - event.changedTouches[0].clientX;
      if (Math.abs(distance) > 40) show((state.index + (distance > 0 ? 1 : -1) + slides.length) % slides.length);
      touchStart = null;
      resume();
    }, { ...options, passive: true });
    root.addEventListener('focusin', (event) => {
      pause();
      const index = slides.indexOf(event.target.closest('.ingredient-slide'));
      if (index >= 0) show(index, false);
    }, options);
    root.addEventListener('focusout', resume, options);
    root.addEventListener('mouseenter', pause, options);
    root.addEventListener('mouseleave', resume, options);
    resume();
  }

  function initialize(scope = document) {
    scope.querySelectorAll('[data-ingredient-slider]').forEach(initSlider);
    scope.querySelectorAll('.itf-text[data-limit]').forEach(limitImageText);
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-ingredient-slider]').forEach(destroySlider);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('.informational');
    if (section) {
      const toggle = section.querySelector('.informational-toggle');
      if (toggle && event.target.closest('.informational-block--expanded_only')) setExpanded(toggle, true);
    }
    const card = event.target.closest('.ingredient-detail-section .ingredient-card');
    if (card) {
      const toggle = card.querySelector('.readmore-btn');
      if (toggle) setExpanded(toggle, true);
    }
    const root = event.target.closest('[data-ingredient-slider]');
    const state = root && sliders.get(root);
    if (state) {
      state.pause();
      const index = state.slides.findIndex((slide) => {
        const editor = slide.getAttribute('data-shopify-editor-block');
        if (!editor) return false;
        try { return JSON.parse(editor).id === event.detail.blockId; } catch { return false; }
      });
      if (index >= 0) state.show(index, false);
    }
  });
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => initialize(), 150);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  if (!customElements.get('nomosu-principles-slider')) {
    customElements.define('nomosu-principles-slider', class extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.events = new AbortController();
        const on = (target, event, handler, extra = {}) => target.addEventListener(event, handler, { ...extra, signal: this.events.signal });
        this.track = this.querySelector('.principles-track');
        if (!this.track) return;
        this.slides = [...this.track.children].filter(slide => !slide.hasAttribute('data-principle-clone'));
        this.index = 0;
        const clone = this.slides.length > 1 && this.slides[0].cloneNode(true);
        if (clone) {
          clone.setAttribute('data-principle-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          [clone, ...clone.querySelectorAll('*')].forEach(node => {
            node.removeAttribute('id');
            node.removeAttribute('data-shopify-editor-block');
            node.removeAttribute('data-block-id');
          });
          this.track.append(clone);
        }
        on(this, 'mouseenter', () => { this.hovered = true; this.pause(); });
        on(this, 'mouseleave', () => { this.hovered = false; this.start(); });
        on(this, 'focusin', () => this.pause());
        on(this, 'focusout', () => this.start());
        on(document, 'visibilitychange', () => this.start());
        on(window, 'resize', () => this.show(this.index % Math.max(1, this.slides.length), false));
        on(this, 'keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          this.pause();
          this.show((this.index + (event.key === 'ArrowRight' ? 1 : -1) + this.slides.length) % this.slides.length);
        });
        let touchStart;
        on(this, 'touchstart', (event) => { touchStart = event.touches[0].clientX; this.pause(); }, { passive: true });
        on(this, 'touchend', (event) => {
          const distance = touchStart - event.changedTouches[0].clientX;
          if (Math.abs(distance) > 40) this.show((this.index + (distance > 0 ? 1 : -1) + this.slides.length) % this.slides.length);
          this.start();
        }, { passive: true });
        on(document, 'shopify:block:select', (event) => {
          const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
          if (index < 0) return;
          this.pause();
          this.show(index, false);
        });
        this.show(0, false);
        this.start();
      }
      show(index, animate = true) {
        if (!this.slides.length) return;
        clearTimeout(this.resetTimer);
        this.index = index;
        this.track.style.transition = animate && !reducedMotion.matches ? 'transform .8s ease' : 'none';
        this.track.style.transform = `translateX(-${index * 100}%)`;
        this.slides.forEach((slide, i) => {
          slide.inert = i !== index % this.slides.length;
          if (slide.inert) slide.setAttribute('aria-hidden', 'true'); else slide.removeAttribute('aria-hidden');
        });
        if (index === this.slides.length) this.resetTimer = setTimeout(() => this.show(0, false), reducedMotion.matches ? 0 : 800);
      }
      pause() { clearInterval(this.timer); }
      start() {
        this.pause();
        if (this.slides.length < 2 || this.dataset.autoplay !== 'true' || this.hovered || this.contains(document.activeElement) || document.hidden || reducedMotion.matches || window.Shopify?.designMode) return;
        this.timer = setInterval(() => this.show(this.index + 1), Math.max(2, Number(this.dataset.seconds) || 5) * 1000);
      }
      disconnectedCallback() {
        this.pause();
        clearTimeout(this.resetTimer);
        this.events?.abort();
        this.events = null;
        this.track?.querySelectorAll('[data-principle-clone]').forEach(clone => clone.remove());
      }
    });
  }

  if (!customElements.get('nomosu-customer-testimonials')) {
    customElements.define('nomosu-customer-testimonials', class extends HTMLElement {
      connectedCallback() {
        if (this.events) return;
        this.events = new AbortController();
        this.track = this.querySelector('.nomosu-customer-carousel');
        this.initialize = () => {
          if (!this.isConnected || !this.track || this.carousel || !window.jQuery?.fn.owlCarousel) return;
          const sanitizeClones = () => {
            this.track.querySelectorAll('.owl-item.cloned').forEach(clone => {
              clone.setAttribute('aria-hidden', 'true');
              clone.inert = true;
              clone.querySelectorAll('[data-shopify-editor-block], [data-block-id], [id]').forEach(node => {
                node.removeAttribute('data-shopify-editor-block'); node.removeAttribute('data-block-id'); node.removeAttribute('id');
              });
            });
          };
          this.carousel = window.jQuery(this.track);
          this.carousel.on('initialized.owl.carousel refreshed.owl.carousel', sanitizeClones);
          this.carousel.owlCarousel({ loop: true, margin: 60, nav: false, dots: false, responsive: { 0: { items: 1 }, 600: { items: 2 }, 1000: { items: 3 } } });
          sanitizeClones();
        };
        document.addEventListener('shopify:block:select', (event) => {
          const slides = [...this.track.querySelectorAll('.owl-item:not(.cloned) > .item')];
          const index = slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
          if (index >= 0) this.carousel?.trigger('to.owl.carousel', [index, 0, true]);
        }, { signal: this.events.signal });
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', this.initialize, { once: true, signal: this.events.signal });
        else this.initialize();
        // Covers a delayed external carousel library without repeatedly polling.
        window.addEventListener('load', this.initialize, { once: true, signal: this.events.signal });
      }
      disconnectedCallback() {
        this.events?.abort();
        this.events = null;
        this.carousel?.trigger('destroy.owl.carousel');
        this.carousel?.off('initialized.owl.carousel refreshed.owl.carousel');
        this.carousel = null;
      }
    });
  }
})();
